import { Setter } from "../../services/ComService";
import { ObjectKind } from "../models/objectKinds";
import { PersonKind } from "../views/Person/PersonKind";
import ItemPersonDTO, { IItemPerson } from "./ItemPersonDTO";
import PropDTO from "./PropDTO";
import { ISeminar, SeminarDTO } from "./SeminarDTO";

export class EventDTO extends SeminarDTO {
  customerId: number = -1
  customerLocationId: number = -1
  seminarId: number = -1
  customerName: string = ''
  customerLocationName: string = ''
  principal?: ItemPersonDTO
  trainers?: ItemPersonDTO[]
  titleSuffix: string = ''
  titlePrefix: string = ''
  appointmentKey: string = ''
  standardDeleteMode: 'delete' | 'trash' = 'trash'
  attendees: ItemPersonDTO[] = []

  constructor(o: IEvent) {
    super(o)
    this.objectKind = ObjectKind.event
    this.customerName = o.customerName || ''
    this.customerLocationName = o.customerLocationName || ''
    if (o.participants) {
      this.attendees = o.participants.map(p => new ItemPersonDTO(p)) 
    }
    if (o.principal) {
      this.principal = new ItemPersonDTO(o.principal)
    }
    if (o.trainers) {
      this.trainers = o.trainers.map(t => new ItemPersonDTO(t))
    }
    if (o.titlePrefix) {
      this.titlePrefix = o.titlePrefix
    }
    if (o.titleSuffix) {
      this.titleSuffix = o.titleSuffix
    }
    if (o.appointmentKey) {
      this.appointmentKey = o.appointmentKey
    }
  }

  afterInit(o: IEvent) {
    // this.seminarId = o.seminarId || -1
    if (o.up && o.up[0]) {
      this.seminarId = parseInt(o.up[0].id + '', 10)
    }
    this.customerId = o.customerId || -1
    this.customerName = o.customerName || ''
    this.customerLocationName = o.customerLocationName || ''
    this.customerLocationId = o.customerLocationId || -1

  }

  public getParticipants() {
    return this.attendees
  }

  public async setStart(s: string) {
    this.startdate = s
    await Setter('spt/setStart', {
      id: this.id,
      startDate: s,
    })
  }

  public async setEnd(s: string) {
    this.enddate = s
    await Setter('spt/setEnd', {
      id: this.id,
      endDate: s,
    })
  }

  public getStartToEndDayCount() {
    const start = this.startdate
    const end = this.enddate
    if (start && end) {
      const s = new Date(start)
      const e = new Date(end)
      const d = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1
      if (d === 1) {
        return `: 1 Tag`
      }
      return `: ${d} Tage`
    }
    return ''
  }

  public countAppointments() {
    return this.getProps('appointment').length
  }

  public async startAndStopToAppointments() {
    const appointments = this.getProps('appointment')
    let newStart = ''
    let newEnd = ''
    appointments.forEach(a => {
      let aStart = a.getV1()
      if (aStart) {
        if (!newStart || aStart < newStart) {
          newStart = aStart
        }
      }
      let aEnd = a.getV2()
      if (aEnd) {
        if (!newEnd || newEnd < aEnd) {
          newEnd = aEnd
        }
      }
    })
    if (newStart) {
      await this.setStart(newStart)
    }
    if (newEnd) {
      await this.setEnd(newEnd)
    }
  }

  public async addAppointment(key2: string) {
    if (this.countAppointments() === 0) {
      // If we do not have an initial appointment - we create it from the current dates
      await this.addProp('appointment', `${new Date().getTime()}-init`, this.getStartString(), this.getEndString())
    }
    // Now we create a new empty appointment:
    await this.addProp('appointment', key2, '', '')
  }

  public async removeAppointment(key2: string) {
    if (!key2) { return }
    await this.removePropByKeys('appointment', key2)
    await this.removePropByKey1(key2)
    await this.startAndStopToAppointments()
  }

  public getAppointmentStart(key2: string): string {
    const appointmentInfo = this.getProp('appointment', key2) as PropDTO
    if (appointmentInfo && appointmentInfo.getV1()) {
      return appointmentInfo.getV1()
    }
    return (this.countAppointments() < 2) ? this.getStartString() : ''
  }

  public getAppointmentEnd(key2: string) {
    const appointmentInfo = this.getProp('appointment', key2) as PropDTO
    if (appointmentInfo && appointmentInfo.getV2()) {
      return appointmentInfo.getV2()
    }
    return (this.countAppointments() < 2) ? this.getEndString() : ''
  }

  public async setAppointmentStart(key2: string, s: string) {
    await this.addProp('appointment', key2, s, this.getAppointmentEnd(key2) || s || '')
    await this.startAndStopToAppointments()
  }

  public async setAppointmentEnd(key2: string, s: string) {
    await this.addProp('appointment', key2, this.getAppointmentStart(key2) || s || '', s)
    await this.startAndStopToAppointments()
  }

  public dateWarnMsg(): {kind: string, msg: string, className: string} {
    if (!this.startdate) {
      return {
        kind: 'info',
        msg: 'Startdatum angeben',
        className: 'warning',
      }
    }
    if (!this.enddate) {
      return {
        kind: 'info',
        msg: 'Enddatum angeben',
        className: 'warning',
      }
    }
    if (this.startdate > this.enddate) {
      return {
        kind: 'error',
        msg: 'Enddatum sollte NACH Startdatum liegen',
        className: 'error',
      }
    }
    return {
      kind: 'okay',
      msg: 'Datumscheck okay',
      className: 'success',
    }
  }

  public async addCustomer(customerId: number) {
    await Setter('spt/event/addCustomer', {
      id: this.id,
      customerId: customerId,
    })
  }

  public async unsetCustomer() {
    await Setter('spt/event/unsetCustomer', {
      id: this.id,
    })
  }

  public async unsetCustomerLocation() {
    await Setter('spt/event/unsetCustomerLocation', {
      id: this.id,
    })
  }

  public async setLocation(locationId: number) {
    await Setter('spt/event/addLocation', {
      id: this.id,
      locationId: locationId,
    })
  }

  public async unsetLocation() {
    await Setter('spt/event/unsetLocation', {
      id: this.id
    })
  }

  public async addPerson(personId: number, personKind?: PersonKind) {
    const personData = await Setter('spt/event/addPerson', {
      id: this.id,
      personId: personId,
      personKind: PersonKind[personKind || PersonKind.generic],
    })
    if (personKind === PersonKind.trainer) {
      const newPerson = new ItemPersonDTO(personData.item)
      const standardTrainerRole = newPerson.getPropV1('trainerProp', 'StandardRole')
      if (standardTrainerRole) {
        await this.addProp('trainerProp', `${personId}`, standardTrainerRole)
      }
    }
  }

  public async removePerson(personId: number, personKind?: PersonKind) {
    await Setter('spt/event/removePerson', {
      id: this.id,
      personId: personId,
      personKind: PersonKind[personKind || PersonKind.generic],
    })
  }

}

export interface IEvent extends ISeminar {
  customerId?: number,
  customerName?: string,
  customerLocationId?: number,
  customerLocationName?: string,
  principal?: IItemPerson,
  trainers?: IItemPerson[],
  titleSuffix?: string,
  titlePrefix?: string,
  appointmentKey?: string,
  participants?: IItemPerson[],
}


